import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { FileTextIcon, XIcon } from 'lucide-react';
import { MAX_FILE_SIZE } from '../../hooks/useCloudinaryUpload';
import { cn } from '../../utils/cn';
import {
  ACCEPT_CONFIG,
  isAcceptedFileType,
  type FileAccept,
} from '../../utils/fileTypes';
import AssetTagInput, { type AssetTagValue } from '../AssetTagInput';

/** Which kinds of files the picker accepts. */
export type FilePickerAccept = FileAccept;

export interface ImageFilePickerProps {
  /** Optional input ID for file input (for unique IDs when multiple instances) */
  inputId?: string;
  /** Callback when a file is selected or cleared */
  onFileChange: (file: File | null) => void;
  /** The currently selected file (controlled) */
  file: File | null;
  /** Which kinds of files to accept. Defaults to 'image'. */
  accept?: FilePickerAccept;
  /** Current tags (controlled) */
  tags?: AssetTagValue[];
  /** Callback when tags change */
  onTagsChange?: (tags: AssetTagValue[]) => void;
  /** Whether to show the tag input. Defaults to true. */
  showTags?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImageFilePicker({
  inputId = 'image-file-picker-input',
  onFileChange,
  file,
  accept = 'image',
  tags = [],
  onTagsChange,
  showTags = true,
}: ImageFilePickerProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptConfig = ACCEPT_CONFIG[accept];

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const validateAndSelect = (selectedFile: File) => {
    if (!isAcceptedFileType(selectedFile, accept)) {
      toastError(acceptConfig.errorMessage);
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      toastError(
        `File too large (${formatFileSize(selectedFile.size)}). Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}.`,
      );
      return;
    }

    // Create preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(selectedFile));
    onFileChange(selectedFile);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSelect(droppedFile);
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSelect(selectedFile);
    }

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Show preview if a file is selected
  if (file && previewUrl) {
    const isImage = file.type.startsWith('image/');
    return (
      <div className="space-y-3">
        <div className="relative overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
          {isImage ? (
            <img
              src={previewUrl}
              alt={file.name}
              className="h-48 w-full object-contain"
            />
          ) : (
            <div className="flex h-48 w-full flex-col items-center justify-center gap-2 text-zinc-500">
              <FileTextIcon className="h-12 w-12" />
              <span className="text-xs font-medium">
                {formatFileSize(file.size)}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
          >
            <XIcon className="h-4 w-4" />
          </button>
          <div className="border-t border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-500">
            {file.name}
          </div>
        </div>
        {showTags && onTagsChange && (
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              Tags
            </label>
            <AssetTagInput
              value={tags}
              onChange={onTagsChange}
              placeholder="Add tags..."
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center rounded border border-dashed px-6 py-10 text-center transition',
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-zinc-300 bg-zinc-100',
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        className="hidden"
        accept={acceptConfig.attr}
        onChange={handleFileInputChange}
      />

      <div>
        <div className="text-sm font-bold text-zinc-600">
          Click or drag and drop your file
        </div>
        <div className="mt-2 text-sm text-zinc-500">
          <label
            htmlFor={inputId}
            className="text-primary cursor-pointer underline-offset-2 hover:underline"
          >
            Browse your computer
          </label>
        </div>
        <div className="mt-2 text-xs text-zinc-400">
          {acceptConfig.label} (Max {formatFileSize(MAX_FILE_SIZE)})
        </div>
      </div>
    </div>
  );
}
